import { t } from "@redotech/redo-i18n";
import GitBranchIcon from "@redotech/redo-icons/git-branch-01.svg";
import { Step } from "@redotech/redo-return-core-common/return-flow";
import { returnProcessingDecisionFromModel } from "@redotech/redo-return-core-common/return-flow-step-from-model";
import {
  isReturnProcessingDecisionStepValid,
  ReturnProcessingDecisionState,
} from "@redotech/redo-return-core-common/return-flow-step-valid";
import {
  DEFAULT_FALSE_LABEL_COLOR,
  DEFAULT_TRUE_LABEL_COLOR,
} from "@redotech/redo-return-core-common/return-flow/decision-label-color";
import { STEP_TYPE_NAMES } from "@redotech/redo-return-core-common/return-flow/step-type-names";
import { BlockLayout } from "@redotech/redo-web/flowchart";
import { produce } from "immer";
import * as React from "react";
import { StepType, StepTypeDetailsProps } from "../step";

export const RETURN_PROCESSING_DECISION = (
  Details: React.ComponentType<
    StepTypeDetailsProps<ReturnProcessingDecisionState>
  >,
): StepType<ReturnProcessingDecisionState, Step.ReturnProcessingDecision> => ({
  Details: (props) => React.createElement(Details, props),
  customTitle: (state) => state.customTitle,
  description: (state) => state.prompt,
  // Two outgoing edges. Index 0 = True (left handle), Index 1 = False (right
  // handle). Mirror the existing condition step's convention so the editor's
  // graph layout positions branches consistently.
  downstream(state) {
    return [
      { id: state.nextTrue, label: state.trueLabel || t("True") },
      { id: state.nextFalse, label: state.falseLabel || t("False") },
    ];
  },
  empty: {
    prompt: "",
    trueLabel: "Yes",
    falseLabel: "No",
    trueLabelColor: DEFAULT_TRUE_LABEL_COLOR,
    falseLabelColor: DEFAULT_FALSE_LABEL_COLOR,
    scope: "return_item",
  },
  fromModel: (model, id) => returnProcessingDecisionFromModel(model, id),
  Icon: GitBranchIcon,
  layout: () => BlockLayout.FULL,
  maxDownstream: () => 2,
  removeConnection(state, targetStepId) {
    return produce(state, (draft) => {
      if (draft.nextTrue === targetStepId) {
        draft.nextTrue = undefined;
      }
      if (draft.nextFalse === targetStepId) {
        draft.nextFalse = undefined;
      }
    });
  },
  stepDeleted(state, stepId) {
    return produce(state, (draft) => {
      if (draft.nextTrue === stepId) {
        draft.nextTrue = undefined;
      }
      if (draft.nextFalse === stepId) {
        draft.nextFalse = undefined;
      }
    });
  },
  title: STEP_TYPE_NAMES[Step.RETURN_PROCESSING_DECISION],
  toModel(state, id) {
    return {
      type: Step.RETURN_PROCESSING_DECISION,
      customTitle: state.customTitle,
      prompt: state.prompt,
      decisionKey: state.decisionKey || undefined,
      trueLabel: state.trueLabel,
      falseLabel: state.falseLabel,
      trueLabelColor: state.trueLabelColor,
      falseLabelColor: state.falseLabelColor,
      imageUrl: state.imageUrl,
      nextTrueId: state.nextTrue ? id(state.nextTrue) : null,
      nextFalseId: state.nextFalse ? id(state.nextFalse) : null,
      scope: state.scope,
    };
  },
  updateConnection(state, handleIndex, targetStepId) {
    return produce(state, (draft) => {
      if (handleIndex === 0) {
        draft.nextTrue = targetStepId;
      } else if (handleIndex === 1) {
        draft.nextFalse = targetStepId;
      } else {
        throw new Error(
          `Invalid handle index ${handleIndex} for return-processing decision step`,
        );
      }
    });
  },
  valid: (state) => isReturnProcessingDecisionStepValid(state).valid,
});
